<template>
	<!-- eslint-disable max-lines-->
	<div>
		<QuestionCard
			:title="title"
			:description="supportingText"
			:show-yes-no-buttons="userFullPermissions"
			:value="value"
			@input="onToggle($event)"
		/>

		<!-- Confirmation Modal for clearing mappings -->
		<Modal
			v-if="showClearMappingsModal"
			max-width="500"
		>
			<template #modal-title>
				Remove {{ moduleDisplayName }} Mappings
			</template>
			<template #modal-content>
				<p class="mb-4">
					You have existing {{ moduleDisplayName.toLowerCase() }} mappings configured.
				</p>
				<p>
					This will permanently remove these mappings when you save this configuration. This action cannot be undone.
				</p>
			</template>
			<template #modal-footer>
				<SecondaryActionButton @click="cancelClearMappings">
					Cancel
				</SecondaryActionButton>
				<PrimaryActionButton @click="confirmClearMappings">
					Remove Mappings
				</PrimaryActionButton>
			</template>
		</Modal>
		<div v-if="value">
			<!-- Add mapping controls - hidden when SCIM is enabled but no groups provisioned yet -->
			<v-row
				v-if="!hideAddMappingUi"
				dense
				class="mb-6"
			>
				<v-col cols="3">
					<Dropdown
						v-model="cassieComponent"
						:disabled="!userFullPermissions"
						:label="dropdownLabel"
						:test-id="`sso-configuration-cassie-component-dropdown-${moduleLabel}`"
						:items="componentItems"
					/>
				</v-col>
				<!-- Entra ID Role Name field - hidden when SCIM is enabled (for non-personal modules) -->
				<v-col
					v-if="!scimEnabled || isPersonalModule"
					cols="3"
				>
					<TextField
						v-model="claimName"
						:disabled="!userFullPermissions"
						:rules="{ max: 255 }"
						:test-id="`sso-configuration-claim-name-${moduleLabel}`"
						:label="claimInputLabel"
						@keydown.enter="addMapping"
					/>
				</v-col>
				<!-- SCIM Groups Dropdown - shown when SCIM is enabled (for non-personal modules) -->
				<v-col
					v-if="showScimGroupDropdown"
					cols="3"
				>
					<Dropdown
						v-model="selectedScimGroupGuid"
						:disabled="!userFullPermissions"
						label="SCIM Group"
						:test-id="`sso-configuration-scim-group-${moduleLabel}`"
						:items="scimGroupItems"
					/>
				</v-col>
				<!-- Regular text field - shown when SCIM is DISABLED (for non-personal modules) -->
				<v-col
					v-else-if="!scimEnabled && !isPersonalModule && mapping.length === 0"
					cols="3"
				>
					<TextField
						v-model="groupName"
						:disabled="!userFullPermissions"
						:label="selectedProvider.claimMappingDisplayName"
						:test-id="`sso-configuration-group-name-${moduleLabel}`"
					/>
				</v-col>
				<v-spacer />
				<v-col
					cols="3"
					class="text-right"
				>
					<SecondaryActionButton
						v-if="userFullPermissions"
						:disabled="!canAdd"
						@click="addMapping"
					>
						Add
					</SecondaryActionButton>
				</v-col>
			</v-row>
			<v-row dense>
				<v-col cols="12">
					<v-simple-table
						dense
						light
						class="sso-mapping-table mt-5"
						role="table"
						aria-label="SSO claim mappings"
					>
						<template #default>
							<thead>
								<tr role="row">
									<th
										class="text-left"
										style="width: 25%;"
										scope="col"
									>
										{{ cassieLabel }}
									</th>
									<th
										class="text-left"
										style="width: 25%;"
										scope="col"
									>
										{{ scimEnabled && !isPersonalModule ? 'SCIM Group Name' : providerClaimLabel }}
									</th>
									<th
										v-if="!isPersonalModule"
										class="text-left"
										style="width: 20%;"
										scope="col"
									>
										{{ scimEnabled ? 'Type' : providerGroupLabel }}
									</th>
									<th
										class="text-center"
										style="width: 56px;"
										scope="col"
									>
										Order
									</th>
									<th
										class="text-center"
										style="width: 120px;"
										scope="col"
									>
										Action
									</th>
								</tr>
							</thead>
							<!-- Always render rows; disable CSS transitions while editing -->
							<transition-group
								tag="tbody"
								name="row"
								:css="editingRowIndex === null"
							>
								<tr
									v-for="(item, index) in mapping"
									:key="itemKey(item, index)"
									class="sso-mapping-row"
									:class="{ 'drop-over': dragOverIndex === index }"
									@dragover.prevent="editingRowIndex === null && onDragOver(index)"
									@dragleave="editingRowIndex === null && onDragLeave(index)"
									@drop="editingRowIndex === null && onDrop(index)"
								>
									<td
										class="text-left"
									>
										<template v-if="editingRowIndex === index">
											<Dropdown
												:key="`cassie-${item.__key || index}`"
												:value="editDraft && editDraft.cassieComponentId"
												:disabled="!userFullPermissions"
												:items="componentItems"
												small
												label=""
												hide-details
												@input="val => onRowCassieChange(index, val)"
											/>
										</template>
										<template v-else>
											{{ item.cassieComponentName }}
										</template>
									</td>
									<td
										class="text-left"
									>
										<template v-if="editingRowIndex === index && (!scimEnabled || isPersonalModule)">
											<TextField
												:key="`ext-${item.__key || index}`"
												:value="editDraft && editDraft.externalComponentName"
												:disabled="!userFullPermissions"
												small
												label=""
												hide-details
												@input="val => onRowDraftChange(index, { externalComponentName: val })"
												@blur="onRowFieldCommit(index)"
												@keydown.enter="onRowFieldCommit(index)"
											/>
										</template>
										<template v-else-if="editingRowIndex === index && scimEnabled && !isPersonalModule">
											<!-- When editing in SCIM mode, show SCIM group dropdown for this column -->
											<Dropdown
												v-model="selectedScimGroupGuid"
												:disabled="!userFullPermissions"
												:items="scimGroupItems"
												small
												label=""
												hide-details
											/>
										</template>
										<template v-else>
											<!-- Display: SCIM Group Name column when SCIM enabled, otherwise externalComponentName -->
											<span :class="{ 'warning-text': scimEnabled && !isScimMappingValid(item) }">
												{{ getScimGroupNameDisplay(item) }}
											</span>
											<v-icon
												v-if="scimEnabled && !isScimMappingValid(item)"
												small
												color="warning"
												class="ml-1"
												title="This mapping was created before SCIM was enabled and may need to be updated"
											>
												mdi-alert
											</v-icon>
										</template>
									</td>
									<td
										v-if="!isPersonalModule"
										class="text-left"
									>
										<template v-if="mapping.length === 1 && userFullPermissions && editingRowIndex === index && !scimEnabled">
											<TextField
												v-model="groupName"
												small
												label=""
												hide-details
												:disabled="!userFullPermissions"
												@keydown.enter.prevent
											/>
										</template>
										<template v-else>
											<!-- Display: Type column when SCIM enabled, otherwise provider group -->
											{{ getTypeColumnDisplay(item) }}
										</template>
									</td>
									<td
										class="text-center"
									>
										{{ item.displayOrder }}
									</td>
									<td
										class="text-center"
									>
										<div style="display:flex;align-items:end;justify-content:end;gap:4px;white-space:nowrap;">
											<template v-if="editingRowIndex === index">
												<v-tooltip top>
													<template #activator="{ on, attrs }">
														<v-btn
															v-if="userFullPermissions"
															icon
															small
															color="primary"
															aria-label="Confirm row edits"
															v-bind="attrs"
															v-on="on"
															@click="confirmRowEdit(index)"
														>
															<v-icon small>
																mdi-check
															</v-icon>
														</v-btn>
													</template>
													<span>
														Confirm changes
													</span>
												</v-tooltip>
												<v-tooltip top>
													<template #activator="{ on, attrs }">
														<v-btn
															v-if="userFullPermissions"
															icon
															small
															color="grey"
															aria-label="Cancel row edits"
															v-bind="attrs"
															v-on="on"
															@click="cancelRowEdit"
														>
															<v-icon small>
																mdi-close
															</v-icon>
														</v-btn>
													</template>
													<span>
														Cancel and revert
													</span>
												</v-tooltip>
											</template>
											<template v-else>
												<template v-if="editingRowIndex === null">
													<v-btn
														v-if="userFullPermissions"
														class="drag-handle"
														icon
														small
														aria-label="Drag to reorder row"
														title="Drag to reorder"
														:draggable="true"
														@dragstart="onDragStart(index)"
														@dragend="onDragEnd"
													>
														<v-icon small>
															mdi-drag-vertical
														</v-icon>
													</v-btn>
													<v-menu
														v-if="userFullPermissions"
														offset-y
														left
													>
														<template #activator="{ on, attrs }">
															<v-btn
																icon
																small
																title="Actions"
																v-bind="attrs"
																v-on="on"
															>
																<v-icon small>
																	mdi-dots-horizontal
																</v-icon>
															</v-btn>
														</template>
														<v-list dense>
															<v-list-item @click="startRowEdit(index)">
																<v-list-item-content>
																	<v-list-item-title>
																		Edit
																	</v-list-item-title>
																</v-list-item-content>
															</v-list-item>
															<v-list-item @click="removeMapping(item)">
																<v-list-item-content>
																	<v-list-item-title>
																		Delete
																	</v-list-item-title>
																</v-list-item-content>
															</v-list-item>
														</v-list>
													</v-menu>
												</template>
											</template>
										</div>
									</td>
								</tr>
							</transition-group>
						</template>
					</v-simple-table>
					<div
						role="status"
						aria-live="polite"
						aria-atomic="true"
						class="sr-only"
					>
						{{ liveMessage }}
					</div>
					<div
						v-if="!mapping.length && !hideAddMappingUi"
						class="mt-2 caption grey--text"
					>
						No mappings added yet. Use the fields above and click Add to create your first mapping.
					</div>
					<div
						v-else-if="!mapping.length && hideAddMappingUi"
						class="mt-2 caption grey--text"
					>
						No mappings have been configured yet.
					</div>
				</v-col>
			</v-row>
			<v-divider />
		</div>
	</div>
</template>

<script>
/* eslint-disable max-lines */
// @ts-nocheck
import { QuestionCard } from '../../../../components/component-library-wrapper.js'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import Modal from '../../../../../../shared/components/modal.vue'
import { brandOptions } from '../../../../../../shared/state/brands.js'

export default {
	components: { QuestionCard, Dropdown, TextField, SecondaryActionButton, PrimaryActionButton, Modal },
	props: {
		moduleId: Number,
		title: String,
		ssoConfiguration: Object,
		lookupData: Object,
		selectedProvider: Object,
		userFullPermissions: Boolean,
		productRoles: { type: Array, default: () => [] },
		value: { type: Boolean, default: false },
		scimEnabled: { type: Boolean, default: false },
		scimGroups: { type: Array, default: () => [] },
		hideAddMappingUi: { type: Boolean, default: false }
	},
	data () {
		const USER_ROLES = 1
		const BRANDS = 2
		const PERSONAL_INFO = 3
		return {
			USER_ROLES,
			BRANDS,
			PERSONAL_INFO,
			mapping: [],
			groupName: null,
			selectedScimGroupGuid: null,
			cassieComponent: null,
			claimName: null,
			liveMessage: '',
			editingRowIndex: null,
			editDraft: null,
			editOriginal: null,
			draggingIndex: null,
			dragOverIndex: null,
			showClearMappingsModal: false
		}
	},
	computed: {
		moduleLabel () {
			if (this.isRolesModule) return 'roles'
			if (this.isBrandsModule) return 'brands'
			return 'personal'
		},
		dropdownLabel () {
			if (this.isRolesModule) return 'Cassie User Role'
			if (this.isBrandsModule) return 'Cassie Brand'
			if (this.isPersonalModule) return 'Cassie Personal Information Field'
			return 'Cassie Component'
		},
		isPersonalModule () {
			const ct = (this.lookupData && this.lookupData.ssoComponentTypes || []).find(x => x.id === this.moduleId)
			return ct ? /personal\s*information/i.test(ct.componentTypeName) : (this.moduleId === this.PERSONAL_INFO)
		},
		isBrandsModule () {
			const ct = (this.lookupData && this.lookupData.ssoComponentTypes || []).find(x => x.id === this.moduleId)
			return ct ? /brand/i.test(ct.componentTypeName) : (this.moduleId === this.BRANDS)
		},
		isRolesModule () {
			const ct = (this.lookupData && this.lookupData.ssoComponentTypes || []).find(x => x.id === this.moduleId)
			return ct ? /role/i.test(ct.componentTypeName) : (this.moduleId === this.USER_ROLES)
		},
		cassieLabel () {
			if (this.isRolesModule) return 'Cassie Role'
			if (this.isBrandsModule) return 'Cassie Brand'
			return 'Cassie Field'
		},
		providerClaimLabel () {
			const p = this.selectedProvider || {}
			if (this.isPersonalModule) {
				return p.personalInformationClaimMappingDisplayName || 'Claim Name'
			}
			return p.claimAttributeDisplayName || 'Provider Claim'
		},
		providerGroupLabel () {
			const p = this.selectedProvider || {}
			return p.claimMappingDisplayName || 'Provider Group'
		},
		claimInputLabel () {
			if (!this.selectedProvider) return ''
			const providerName = (this.selectedProvider.ssoProviderName || this.selectedProvider.name || '').toLowerCase()
			const isOkta = providerName === 'okta'
			if (isOkta && this.isPersonalModule) {
				return this.selectedProvider.claimMappingDisplayName || ''
			}
			return this.selectedProvider.claimAttributeDisplayName || ''
		},
		componentItems () {
			if (this.isRolesModule) {
				return this.productRoles.map(r => ({ text: r.name, value: r.roleId }))
			}
			if (this.isBrandsModule) {
				return brandOptions.value
			}
			if (this.isPersonalModule) {
				const base = [
					{ text: 'Forename', value: 1 },
					{ text: 'Surname', value: 2 }
				]
				if (this.ssoConfiguration.productId === 2) base.push({ text: 'Email Address', value: 5 })
				if (this.ssoConfiguration.productId === 1) base.push({ text: 'Telephone Number', value: 3 }, { text: 'Mobile Number', value: 4 })
				return base
			}
			return []
		},
		canAdd () {
			if (!this.userFullPermissions) return false
			const needsGroup = !this.isPersonalModule
			// When SCIM is enabled, check for selected SCIM group; otherwise check groupName
			const hasGroup = needsGroup
				? (this.scimEnabled ? !!this.selectedScimGroupGuid : !!this.groupName)
				: true
			const hasComponent = this.cassieComponent !== null && this.cassieComponent !== undefined
			// When SCIM is enabled for non-personal modules, claimName is not required
			const needsClaimName = !this.scimEnabled || this.isPersonalModule
			const hasClaimName = needsClaimName ? !!this.claimName : true
			if (!hasGroup || !hasComponent || !hasClaimName) return false
			// Check if this would be a duplicate mapping
			return !this.wouldBeDuplicate
		},
		wouldBeDuplicate () {
			// Check if the current selection would create a duplicate mapping
			if (this.cassieComponent === null || this.cassieComponent === undefined) return false
			return this.mapping.some(m => {
				if (m.componentTypeId !== this.moduleId) return false
				if (m.cassieComponentId !== this.cassieComponent) return false
				// For SCIM mode, check by scimGroupGuid; otherwise check externalComponentName
				if (this.scimEnabled && !this.isPersonalModule) {
					return m.scimGroupGuid === this.selectedScimGroupGuid
				}
				return m.externalComponentName === this.claimName
			})
		},
		supportingText () {
			if (!this.value) return ''
			const provider = this.selectedProvider || {}
			const claimAttr = provider.claimAttributeDisplayName
			const claimGroup = provider.claimMappingDisplayName
			if (this.isRolesModule) {
				if (this.scimEnabled) {
					return `Map a Cassie User Role to an ${claimAttr} found within the specified SCIM Group.`
				}
				return `Map a Cassie User Role to an ${claimAttr} found within the specified ${claimGroup} in your SSO token.`
			}
			if (this.isBrandsModule) {
				if (this.scimEnabled) {
					return `Map a Cassie Brand to an ${claimAttr} found within the specified SCIM Group.`
				}
				return `Map a Cassie Brand to an ${claimAttr} found within the specified ${claimGroup} in your SSO token.`
			}
			if (this.isPersonalModule) {
				return `Map each Cassie Personal Information Field to the corresponding ${claimAttr} found in the root of the SSO token.`
			}
			return ''
		},
		scimGroupItems () {
			return (this.scimGroups || []).map(g => ({
				text: g.displayName,
				value: g.groupGuid
			}))
		},
		showScimGroupDropdown () {
			// Show SCIM group dropdown when SCIM is enabled for non-personal modules
			// (regardless of whether mappings already exist)
			return this.scimEnabled && !this.isPersonalModule && this.scimGroupItems.length > 0
		},
		selectedScimGroupName () {
			if (!this.selectedScimGroupGuid) return ''
			const group = (this.scimGroups || []).find(g => g.groupGuid === this.selectedScimGroupGuid)
			return group ? group.displayName : ''
		},
		moduleDisplayName () {
			if (this.isRolesModule) return 'User Role'
			if (this.isBrandsModule) return 'Brand'
			if (this.isPersonalModule) return 'Personal Information'
			return 'Mapping'
		},
		hasExistingMappings () {
			return this.mapping && this.mapping.length > 0
		}
	},
	watch: {
		value: {
			immediate: true,
			handler (val) {
				if (val) this.initFromExisting()
			}
		},
		'ssoConfiguration.mappedClaims': {
			deep: true,
			handler () {
				if (this.value && this.editingRowIndex === null) this.initFromExisting()
			}
		},
		groupName (val) {
			if (this.isPersonalModule) return
			if (this.scimEnabled) return // Don't use groupName watcher when SCIM is enabled
			if (!Array.isArray(this.mapping) || this.mapping.length === 0) return
			this.mapping = this.mapping.map(m => ({ ...m, componentTypeExternalName: val }))
			this.saveCurrentMapping()
		},
		selectedScimGroupGuid (val) {
			// In SCIM mode, each mapping has its own SCIM group, so we should NOT
			// update existing mappings when the dropdown selection changes.
			// The dropdown is only used for adding NEW mappings.
			// This watcher is kept empty intentionally - no action needed.
		}
	},
	methods: {
		getGroupDisplayName (item) {
			if (item.componentTypeExternalName === 'personal_information') return '—'
			// If SCIM is enabled and we have a SCIM group name stored, show that
			if (this.scimEnabled && item.scimGroupName) {
				return item.scimGroupName
			}
			// If SCIM is enabled and componentTypeExternalName is 'scim_group', try to look up the name
			if (this.scimEnabled && item.componentTypeExternalName === 'scim_group' && item.scimGroupGuid) {
				const group = (this.scimGroups || []).find(g => g.groupGuid === item.scimGroupGuid)
				return group ? group.displayName : item.componentTypeExternalName
			}
			return item.componentTypeExternalName
		},
		getScimGroupNameDisplay (item) {
			// For non-SCIM mode or personal module, show the externalComponentName (Entra ID Role Name)
			if (!this.scimEnabled || this.isPersonalModule) {
				return item.externalComponentName
			}
			// For SCIM mode: show the SCIM group name
			// If this is a proper SCIM mapping, show the scimGroupName
			if (item.scimGroupName) {
				return item.scimGroupName
			}
			// If we have a scimGroupGuid, try to look up the name
			if (item.scimGroupGuid) {
				const group = (this.scimGroups || []).find(g => g.groupGuid === item.scimGroupGuid)
				if (group) return group.displayName
			}
			// For legacy mappings (before SCIM was enabled), show the externalComponentName
			// This is the old "Entra ID Role Name" value
			return item.externalComponentName || '—'
		},
		getTypeColumnDisplay (item) {
			// For non-SCIM mode, show the provider group (componentTypeExternalName)
			if (!this.scimEnabled) {
				if (item.componentTypeExternalName === 'personal_information') return '—'
				return item.componentTypeExternalName
			}
			// For SCIM mode:
			// If this is a proper SCIM mapping, show 'scim_group'
			if (item.componentTypeExternalName === 'scim_group') {
				return 'scim_group'
			}
			// For legacy mappings, show the old componentTypeExternalName (Entra ID Group Name)
			if (item.componentTypeExternalName === 'personal_information') return '—'
			return item.componentTypeExternalName
		},
		isScimMappingValid (item) {
			// A mapping is valid for SCIM if:
			// 1. It's a proper SCIM mapping (componentTypeExternalName === 'scim_group')
			// 2. OR the externalComponentName matches a known SCIM group display name
			if (!this.scimEnabled) return true
			if (item.componentTypeExternalName === 'scim_group') return true
			// For legacy mappings, check if the externalComponentName matches any SCIM group
			const matchingGroup = (this.scimGroups || []).find(g =>
				g.displayName === item.externalComponentName
			)
			return !!matchingGroup
		},
		startRowEdit (index) {
			if (!this.userFullPermissions) return
			this.editOriginal = JSON.parse(JSON.stringify(this.mapping[index] || {}))
			this.editDraft = JSON.parse(JSON.stringify(this.mapping[index] || {}))
			this.editingRowIndex = index
			// If SCIM enabled, set the selectedScimGroupGuid from the item being edited
			if (this.scimEnabled && this.mapping[index]?.scimGroupGuid) {
				this.selectedScimGroupGuid = this.mapping[index].scimGroupGuid
			}
		},
		confirmRowEdit (index) {
			if (this.editingRowIndex !== index) return

			const draft = this.editDraft || {}
			const meta = this.componentItems.find(i => i.value === draft.cassieComponentId)
			// Trim whitespace from externalComponentName to prevent SSO matching issues
			const trimmedDraft = {
				...draft,
				externalComponentName: draft.externalComponentName?.trim() || ''
			}
			const updated = { ...this.mapping[index], ...trimmedDraft, cassieComponentName: meta ? meta.text : trimmedDraft.cassieComponentName }

			// If SCIM enabled, update the SCIM group info
			if (this.scimEnabled && !this.isPersonalModule) {
				updated.componentTypeExternalName = 'scim_group'
				updated.scimGroupGuid = this.selectedScimGroupGuid
				updated.scimGroupName = this.selectedScimGroupName
				// Store the SCIM group name in externalComponentName for the API
				updated.externalComponentName = this.selectedScimGroupName
				updated.scimExternalId = this.selectedScimGroupGuid // Send SCIM group GUID as scimExternalId for the API
			}

			this.$set(this.mapping, index, updated)
			this.editingRowIndex = null
			this.editDraft = null
			this.editOriginal = null
			this.saveCurrentMapping()
		},
		cancelRowEdit () {
			if (this.editingRowIndex === null) return
			const idx = this.editingRowIndex

			if (this.editOriginal) this.$set(this.mapping, idx, this.editOriginal)
			this.editingRowIndex = null
			this.editDraft = null
			this.editOriginal = null
		},
		onRowFieldChange (index, field) {
			if (!this.mapping[index]) return
			if (this.editingRowIndex !== index) this.saveCurrentMapping()
		},
		onRowCassieChange (index, newId) {
			const itemMeta = this.componentItems.find(i => i.value === newId)
			if (this.editingRowIndex === index) {
				this.editDraft = { ...this.editDraft, cassieComponentId: newId, cassieComponentName: itemMeta ? itemMeta.text : '' }
			} else if (this.mapping[index]) {
				this.mapping[index].cassieComponentId = newId
				this.mapping[index].cassieComponentName = itemMeta ? itemMeta.text : ''
				this.saveCurrentMapping()
			}
		},
		onRowDraftChange (index, patch) {
			if (this.editingRowIndex !== index) return
			this.editDraft = { ...this.editDraft, ...patch }
		},
		itemKey (item, index) {
			return item && item.__key ? item.__key : `idx-${index}`
		},
		onDragStart (index) {
			this.draggingIndex = index
		},
		onDragOver (index) {
			this.dragOverIndex = index
		},
		onDragLeave (index) {
			if (this.dragOverIndex === index) this.dragOverIndex = null
		},
		onDrop (index) {
			if (this.draggingIndex == null) return
			const from = this.draggingIndex
			const to = index
			this.draggingIndex = null
			this.dragOverIndex = null
			if (from === to) return
			this.move(from, to)
		},
		onDragEnd () {
			this.draggingIndex = null
			this.dragOverIndex = null
		},
		onToggle (val) {
			// If turning off and there are existing mappings, show confirmation modal
			if (!val && this.hasExistingMappings) {
				this.showClearMappingsModal = true
				return
			}
			this.$emit('input', val)
			if (val) this.initFromExisting()
		},
		cancelClearMappings () {
			// Keep mappings and keep toggle as Yes
			// Re-emit true to force QuestionCard to re-render with correct state
			this.showClearMappingsModal = false
			this.$emit('input', true)
		},
		confirmClearMappings () {
			// Clear mappings and set toggle to No
			this.mapping = []
			this.groupName = null
			this.selectedScimGroupGuid = null
			this.saveCurrentMapping()
			this.showClearMappingsModal = false
			this.$emit('input', false)
		},
		initFromExisting () {
			const existing = (this.ssoConfiguration.mappedClaims || []).find(mc => (mc.claims && mc.claims[0] && mc.claims[0].componentTypeId) === this.moduleId)
			const claims = existing ? (existing.claims || []) : []
			const copy = claims.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map(c => ({ __key: c.__key || `${c.componentTypeId}-${c.cassieComponentId}-${c.externalComponentName}-${c.displayOrder}-${Math.random().toString(36).slice(2)}`, ...c }))
			this.mapping = copy

			// Set groupName or selectedScimGroupGuid based on existing data
			if (copy[0]) {
				if (this.scimEnabled && copy[0].scimGroupGuid) {
					// Existing SCIM mapping - use the stored SCIM group
					this.selectedScimGroupGuid = copy[0].scimGroupGuid
					this.groupName = null
				} else if (this.scimEnabled && !this.isPersonalModule) {
					// SCIM is enabled but existing mappings are from before SCIM was enabled
					// Clear groupName and let user select a SCIM group for new mappings
					this.groupName = null
					this.selectedScimGroupGuid = null
				} else if (copy[0].componentTypeExternalName !== 'personal_information') {
					// Non-SCIM mode - use the componentTypeExternalName as groupName
					this.groupName = copy[0].componentTypeExternalName
					this.selectedScimGroupGuid = null
				}
			}
		},
		saveCurrentMapping () {
			const componentType = (this.lookupData.ssoComponentTypes || []).find(ct => ct.id === this.moduleId)
			const componentTypeName = componentType ? componentType.componentTypeName : ''
			const updated = { ...this.ssoConfiguration, mappedClaims: [...(this.ssoConfiguration.mappedClaims || [])] }
			const idx = updated.mappedClaims.findIndex(c => c.cassieModuleName === componentTypeName)
			if (this.mapping.length === 0) {
				if (idx >= 0) updated.mappedClaims.splice(idx, 1)
			} else if (idx >= 0) {
				updated.mappedClaims[idx] = { ...updated.mappedClaims[idx], claims: this.mapping }
			} else {
				updated.mappedClaims.push({ cassieModuleName: componentTypeName, claims: this.mapping })
			}
			this.$emit('update:ssoConfiguration', updated)
		},
		addMapping () {
			if (!this.canAdd) return
			const item = this.componentItems.find(i => i.value === this.cassieComponent)

			// Determine componentTypeExternalName and scimGroupGuid based on SCIM mode
			let componentTypeExternalName
			let scimGroupGuid = null
			let scimGroupName = null
			// Trim whitespace from claimName to prevent SSO matching issues
			let externalComponentName = this.claimName?.trim() || ''

			if (this.isPersonalModule) {
				componentTypeExternalName = 'personal_information'
			} else if (this.scimEnabled) {
				componentTypeExternalName = 'scim_group'
				scimGroupGuid = this.selectedScimGroupGuid
				scimGroupName = this.selectedScimGroupName
				// When SCIM is enabled, store the SCIM group name in externalComponentName for the API
				externalComponentName = scimGroupName
			} else {
				// Trim whitespace from groupName
				componentTypeExternalName = this.groupName?.trim() || ''
			}

			const claim = {
				__key: `${this.moduleId}-${this.cassieComponent}-${externalComponentName || scimGroupGuid}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
				cassieComponentId: this.cassieComponent,
				cassieComponentName: item ? item.text : '',
				componentTypeExternalName,
				componentTypeId: this.moduleId,
				displayOrder: this.mapping.length + 1,
				externalComponentName,
				ssoClaimId: 0,
				scimGroupGuid,
				scimGroupName,
				scimExternalId: scimGroupGuid // Send SCIM group GUID as scimExternalId for the API
			}
			// Check for duplicate: same Cassie component + same SCIM group (or same external name in non-SCIM mode)
			const isDuplicate = this.mapping.some(m => {
				if (m.componentTypeId !== claim.componentTypeId) return false
				if (m.cassieComponentId !== claim.cassieComponentId) return false
				// For SCIM mode, check by scimGroupGuid; otherwise check externalComponentName
				if (this.scimEnabled && !this.isPersonalModule) {
					return m.scimGroupGuid === claim.scimGroupGuid
				}
				return m.externalComponentName === claim.externalComponentName
			})
			if (isDuplicate) {
				// Silently prevent duplicate - user feedback could be added here if needed
				return
			}
			this.mapping.push(claim)
			if (this.isPersonalModule) this.groupName = null
			this.cassieComponent = null
			this.claimName = null
			this.saveCurrentMapping()
		},
		removeMapping (claim) {
			this.mapping = this.mapping.filter(m => m !== claim).map((m, idx) => ({ ...m, displayOrder: idx + 1 }))
			this.saveCurrentMapping()
		},
		move (from, to) {
			const copy = this.mapping.slice()
			const [moved] = copy.splice(from, 1)
			copy.splice(to, 0, moved)
			this.mapping = copy.map((m, idx) => ({ ...m, displayOrder: idx + 1 }))

			const label = `${moved.cassieComponentName} – ${moved.externalComponentName}`
			this.liveMessage = `Moved ${label} to position ${to + 1}`
			this.saveCurrentMapping()
		},
		onRowFieldCommit (index) {
			if (!this.mapping[index]) return
			this.saveCurrentMapping()
		}
	}
}
</script>

<style lang="scss" scoped>
.row-move {
  transition: transform 160ms ease;
}

@media (prefers-reduced-motion: reduce) {
  .row-move {
    transition: none !important;
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.drag-handle {
  cursor: grab;
}

.sso-mapping-row.drop-over {
  outline: 2px dashed var(--v-primary-base, var(--global-color-blue-darken-2));
  outline-offset: -2px;
}

.warning-text {
  color: var(--v-warning-base, #fb8c00);
}
</style>
